import { Building2, LogIn, LogOut, MapPin, MapPinOff } from "lucide-react";
import type { LngLatBoundsLike } from "maplibre-gl";
import MapGL, { Marker } from "react-map-gl/maplibre";

import { Badge } from "@/components/ui/badge";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useWorksite } from "@/features/canteiros/hooks/use-worksite.ts";
import { OSM_STYLE } from "@/lib/osm-style";
import { useActiveContext } from "@/store/use-active-context";
import type { TimeEntry } from "../../types";
import {
	farthestDistance,
	formatDistance,
	isFar,
	type PunchKind,
	type PunchLocation,
	punchLocations,
	toCoordinate,
} from "../../utils/location";

const MAX_ZOOM = 16;
const BOUNDS_PADDING = 48;

const PUNCH_LABEL: Record<PunchKind, string> = {
	in: "Entrada",
	out: "Saída",
};

interface Point {
	lat: number;
	lng: number;
}

function boundsOf(points: Point[]): LngLatBoundsLike {
	const lats = points.map((point) => point.lat);
	const lngs = points.map((point) => point.lng);
	return [
		[Math.min(...lngs), Math.min(...lats)],
		[Math.max(...lngs), Math.max(...lats)],
	];
}

function PunchMap({
	punches,
	worksite,
}: {
	punches: PunchLocation[];
	worksite: Point | undefined;
}) {
	const points: Point[] = punches.map((punch) => punch.location);
	if (worksite) points.push(worksite);

	return (
		<div className="punch-map">
			<MapGL
				mapStyle={OSM_STYLE}
				initialViewState={{
					bounds: boundsOf(points),
					fitBoundsOptions: { padding: BOUNDS_PADDING, maxZoom: MAX_ZOOM },
				}}
				style={{ position: "absolute", inset: 0 }}
			>
				{worksite && (
					<Marker
						longitude={worksite.lng}
						latitude={worksite.lat}
						anchor="bottom"
					>
						<span className="punch-map__pin" data-kind="worksite">
							<Building2 />
						</span>
					</Marker>
				)}
				{punches.map((punch) => (
					<Marker
						key={punch.kind}
						longitude={punch.location.lng}
						latitude={punch.location.lat}
						anchor="bottom"
					>
						<span className="punch-map__pin" data-kind={punch.kind}>
							{punch.kind === "in" ? <LogIn /> : <LogOut />}
						</span>
					</Marker>
				))}
			</MapGL>
		</div>
	);
}

function LocationPopover({ punches }: { punches: PunchLocation[] }) {
	const activeCanteiroId = useActiveContext((state) => state.activeCanteiroId);
	const { data } = useWorksite(activeCanteiroId ?? undefined);
	const lat = toCoordinate(data?.address.lat);
	const lng = toCoordinate(data?.address.lng);
	const worksite =
		lat !== undefined && lng !== undefined ? { lat, lng } : undefined;

	return (
		<PopoverContent className="interval-location__popover" align="start">
			<PunchMap punches={punches} worksite={worksite} />
			<ul className="interval-location__legend">
				{punches.map((punch) => (
					<li key={punch.kind} data-kind={punch.kind}>
						{punch.kind === "in" ? <LogIn /> : <LogOut />}
						<span>{PUNCH_LABEL[punch.kind]}</span>
						<span
							className="interval-location__legend-distance"
							data-distance={isFar(punch.location.distanceM) ? "far" : "near"}
						>
							{formatDistance(punch.location.distanceM) ||
								"distância desconhecida"}
						</span>
					</li>
				))}
				{worksite && (
					<li data-kind="worksite">
						<Building2 />
						<span>Canteiro</span>
					</li>
				)}
			</ul>
		</PopoverContent>
	);
}

export function IntervalLocation({
	entry,
	modal,
}: {
	entry: TimeEntry;
	modal?: boolean;
}) {
	const punches = punchLocations(entry);

	if (punches.length === 0) {
		return (
			<Badge
				variant="outline"
				className="interval-location"
				data-location="missing"
			>
				<MapPinOff />
				Sem localização
			</Badge>
		);
	}

	const distance = farthestDistance(punches);
	const far = isFar(distance);

	return (
		<Popover modal={modal}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="interval-location"
					data-location="present"
					data-distance={far ? "far" : "near"}
					aria-label="Ver localização do ponto no mapa"
				>
					<MapPin />
					<span className="interval-location__distance">
						{formatDistance(distance) || "Com localização"}
					</span>
				</button>
			</PopoverTrigger>
			<LocationPopover punches={punches} />
		</Popover>
	);
}
